import React, { useCallback, useState, useMemo, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Formik, Form, FormikProps } from 'formik'
import * as yup from 'yup'
import { Flex, Box } from 'rebass'
import { GridColDef, GridApi, GridSelectionModel } from '@mui/x-data-grid-pro'

import _ from 'lodash'
import { SUBMISSION_VIEW_ENUM, SubmissionState, exportSubmissions, massSubmission } from 'state/submissions'
import {
    TagOption,
    DisplayGroupedOption,
    selectFollowerTierOptions,
    selectCampaignTaskTypes,
    selectTaskPlatformContentTypesByName,
    selectIsEngagementStepTypeHelper,
    GLOBAL_MODAL_TYPE,
    openModalWithProps,
    closeModal,
} from 'state/constants'
import { approveSubmission, requestEditsForSubmission, rejectSubmission } from 'state/submissions'
import { TaskState, EngagementId } from 'state/tasks'
import { selectRequestsAreLoading } from 'state'
import { STATE_KEYS } from 'state/requests'
import {
    selectSidebarStep,
    selectSelectedCampaign,
    selectIsMultiSelectMode,
    setMassSubmissionUuids,
    selectSearchablePlatforms,
    selectSelectedCampaignUuid,
    selectMassSubmissionUuids,
    selectSelectedCampaignSummary,
    selectCheckCanReviewSubmissionByStep,
    MAX_PAGE_SIZE,
    selectActivePageNum,
    selectSidebarTask,
    makeNextStepSelector,
    selectSubmissionListParams,
} from 'state/review'
import withDecisionEditor from 'components/Submissions/Review/common/DecisionEditor'
import {
    getColumns as getScreenshotColumns,
    getRows as getScreenshotRows,
    RowsProps as ScreenshotRowsProps,
    ApproveSubmission as ApproveScreenshotSubmission,
} from './ScreenshotTableDef'
import {
    getColumns as getEngagementScreenshotColumns,
    getRows as getEngagementScreenshotRows,
    RowsProps as EngagementScreenshotRowsProps,
    ApproveSubmission as ApproveEngagementScreenshotSubmission,
} from './EngagementScreenshotTableDef'
import {
    getColumns as getUrlColumns,
    getRows as getUrlRows,
    RowsProps as UrlRowsProps,
    ApproveUrlSubmission,
    ApprovePostAndUrlSubmission,
} from './UrlTableDef'
import {
    getColumns as getEngagementPostColumns,
    getRows as getEngagementPostRows,
    RowsProps as EngagementPostRowsProps,
    ApproveSubmission as ApproveEngagementPostSubmission,
} from './EngagementPostTableDef'
import * as CustomEngagementTableDef from './CustomEngagementTableDef'
import PartiTable from 'components/Shared/MuiTable/PartiTable'
import { theme } from 'theme'
import { selectPartiposterProfiles } from 'state/partiposter'
import { TASK_TYPE_ENUM, STEP_TYPE_ENUM, TaskTypes } from 'services/api/constants'
import EditRequestHistoryButton from 'components/Submissions/Review/Submissions/EditRequestHistory'
import { usePermission } from 'hooks/permissions'
import { CampaignSubmission } from 'components/Shared/Permission/enums'
import RectangleButton from 'particles/Button/RectangleButton'
import BaseDropdown, { DropdownProps as BaseDropdownProps } from 'particles/Dropdown'
import { useTranslation } from 'react-i18next'
import MenuItem from 'particles/MenuItem'
import { EmptySearchResult } from 'particles/MenuItem/Search'
import Text from 'particles/Text'
import styled from 'particles/themes/styled'
import { HandleMassSubmissionFunction } from '../../common/types'
import { dateIsInPast } from 'services/dates'
import ExportModal from '../../common/ExportModal'
import { RequestExportSubmissionsPayload } from 'services/api/submissions'
import { HandleEditTagChange, HandleRejectTagChange } from 'components/utils/review'
import { TableViewType } from 'components/Shared/MuiTable'
import Inputs from 'components/Inputs'
import { CAMPAIGN_STATUS_ENUM } from 'state/campaigns'
import useBroadcastChannel from 'hooks/broadcastChannel'

interface DropdownProps extends BaseDropdownProps {
    displayOptions: DisplayGroupedOption[]
    tag_uuids?: string[]
    onTagChange?: (options: TagOption[]) => void
}

interface ExtendedTagOption extends TagOption {
    isDisabled?: boolean
}

const StyledDropdownValue = styled(Text)`
    text-overflow: ellipsis;
    overflow: hidden;
    flex: 1 0 0;
    white-space: nowrap;
`

export const Dropdown: React.FC<DropdownProps> = ({
    label,
    subtext,
    placeholder,
    tag_uuids,
    onTagChange,
    displayOptions,
    size = 'm',
}) => {
    const [t] = useTranslation()
    const [searchValue, setSearchValue] = useState<string>('')
    const [selectedOptionsLabel, setSelectedOptionsLabel] = useState<string>('')

    const handleSearchValueChange = useCallback((value: string) => {
        setSearchValue(value)
    }, [])

    const handleClearSearch = useCallback(() => {
        setSearchValue('')
    }, [])

    // Helper function to get tag option from the selection
    const handleOnChange = useCallback(
        value => {
            const selectedOptions: TagOption[] = []
            displayOptions.forEach(displayOption =>
                displayOption.options.forEach(option => {
                    if (value.includes(option.uuid)) {
                        selectedOptions.push(option)
                    }
                }),
            )
            onTagChange?.(selectedOptions)
            setSelectedOptionsLabel(selectedOptions.map(option => option.label).join(', '))
        },
        [onTagChange],
    )

    const optionComponents = useMemo(() => {
        const components: React.ReactNode[] = []
        components.push(
            <MenuItem.Search
                onSearch={handleSearchValueChange}
                onClearSearch={handleClearSearch}
                placeholder={t('placeholderText.searchReason')}
                searchValue={searchValue}
            />,
        )
        displayOptions.forEach((displayOption, index) => {
            if (index > 0) {
                components.push(<MenuItem.Divider />)
            }
            components.push(<MenuItem.Title text={displayOption.label} />)
            const filteredOptions = displayOption.options.filter(option =>
                option.label.toLowerCase().includes(searchValue.toLowerCase()),
            )

            if (filteredOptions.length === 0) {
                components.push(<EmptySearchResult />)
            } else {
                filteredOptions.forEach((option: ExtendedTagOption) =>
                    components.push(
                        <MenuItem.Checkbox
                            text={option.label}
                            value={option.uuid}
                            isDisabled={!tag_uuids?.includes(option.uuid) && option.isDisabled}
                        />,
                    ),
                )
            }
        })
        return components
    }, [displayOptions, searchValue, tag_uuids])
    return (
        <BaseDropdown
            size={size}
            label={label}
            subtext={subtext}
            placeholder={placeholder}
            value={tag_uuids}
            multiple
            onChange={handleOnChange}
            renderValue={() => <StyledDropdownValue scale="bodyS400">{selectedOptionsLabel}</StyledDropdownValue>}
        >
            {optionComponents}
        </BaseDropdown>
    )
}

const SubmissionContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing[200]};
`

const EditSubmission: React.FC<any> = ({ handleEdit, handleClear, data }) => {
    const [newT] = useTranslation()

    const validation = yup.object({
        comment: yup
            .string()
            .nullable()
            .max(10000, ({ max }) =>
                newT(
                    'errorMessage.validation.enterLessThanCharacterLimit',
                    'Please enter less than {{max}} characters.',
                    {
                        max,
                    },
                ),
            )
            .required(
                newT('errorMessage.validation.enterRequestEditReason', 'Please enter a reason for the Partiposter'),
            ),
    })

    return (
        <Formik
            initialValues={{
                comment: '',
            }}
            validationSchema={validation}
            enableReinitialize={true}
            onSubmit={(values: { comment: string | null }, actions): void => {
                handleEdit(data.uuid, values.comment)
                handleClear()
                actions.setSubmitting(false)
            }}
        >
            {(formikBag: FormikProps<any>) => {
                return (
                    <Form>
                        <SubmissionContainer>
                            <Inputs.TextArea
                                name="comment"
                                placeholder={newT(
                                    'submission.review.reasonsForRequestEditSubtitle',
                                    'Select a reason above and edit the comment here.',
                                )}
                                minTextAreaHeight={120}
                                maxTextAreaHeight={210}
                            />
                            <EditRequestHistoryButton
                                submissionProgressUuid={data.uuid}
                                partiposterId={data.partiposterId}
                            />
                        </SubmissionContainer>
                        <Flex my={theme.newSpacing.s}>
                            <RectangleButton
                                form="outlined"
                                size="l"
                                color="neutral"
                                label={newT('button.cancel')}
                                onClick={handleClear}
                            />
                            <Box mx="auto" />
                            <RectangleButton
                                form="filled"
                                size="l"
                                color="neutral"
                                type="submit"
                                label={newT('button.requestEdits')}
                            />
                        </Flex>
                    </Form>
                )
            }}
        </Formik>
    )
}

const RejectSubmission: React.FC<any> = ({ handleReject, handleClear, data, handleRejectTagChange }) => {
    const [newT] = useTranslation()
    const validation = yup.object({
        comment: yup
            .string()
            .nullable()
            .max(10000, ({ max }) =>
                newT(
                    'errorMessage.validation.enterLessThanCharacterLimit',
                    'Please enter less than {{max}} characters.',
                    {
                        max,
                    },
                ),
            )
            .required(
                newT('errorMessage.validation.enterRequestEditReason', 'Please enter a reason for the Partiposter'),
            ),
    })
    return (
        <Formik
            initialValues={{
                comment: '',
            }}
            validationSchema={validation}
            enableReinitialize={true}
            onSubmit={(values: { comment: string | null }, actions): void => {
                handleReject(data.uuid, values.comment)
                handleClear()
                actions.setSubmitting(false)
            }}
        >
            {(formikBag: FormikProps<any>) => {
                return (
                    <Form>
                        <SubmissionContainer>
                            <Inputs.TextArea
                                name="comment"
                                placeholder={newT(
                                    'submission.review.reasonsForRequestEditSubtitle',
                                    'Select a reason above and edit the comment here.',
                                )}
                                minTextAreaHeight={120}
                                maxTextAreaHeight={210}
                            />
                        </SubmissionContainer>
                        <Flex my={theme.newSpacing.s}>
                            <RectangleButton
                                form="outlined"
                                size="l"
                                color="neutral"
                                label={newT('button.cancel')}
                                onClick={handleClear}
                            />
                            <Box mx="auto" />
                            <RectangleButton
                                form="filled"
                                size="l"
                                color="neutral"
                                type="submit"
                                label={newT('button.reject')}
                            />
                        </Flex>
                    </Form>
                )
            }}
        </Formik>
    )
}

interface VerifyTableProps {
    task: TaskState
    campaignUuid: string
    showLinkInBio: boolean
    submissions: SubmissionState[]
    handleEditTagChange: HandleEditTagChange
    handleRejectTagChange: HandleRejectTagChange
}

const VerifyTable: React.FC<any> = ({ submissions, decisionEditorState, setDecisionEditorState, columns, rows }) => {
    const [newT] = useTranslation()
    const isEngagementStepType = useSelector(selectIsEngagementStepTypeHelper)
    const dispatch = useDispatch()
    const submissionLoading = useSelector(selectRequestsAreLoading([STATE_KEYS.SubmissionsList]))
    const massSubmissionUuids = useSelector(selectMassSubmissionUuids)
    const selectedStep = useSelector(selectSidebarStep)
    const selectedTask = useSelector(selectSidebarTask)
    const isMultiSelectMode = useSelector(selectIsMultiSelectMode)
    const selectedCampaignUuid = useSelector(selectSelectedCampaignUuid)
    const selectedCampaignSummary = useSelector(selectSelectedCampaignSummary)
    const isSubmissionExportRequesting = useSelector(selectRequestsAreLoading([STATE_KEYS.ExportSubmissions]))
    const submissionListParams = useSelector(selectSubmissionListParams)
    const activePageNum = useSelector(selectActivePageNum)
    const checkCanReviewSubmission = useSelector(selectCheckCanReviewSubmissionByStep)
    const campaignEnded = selectedCampaignSummary?.status === CAMPAIGN_STATUS_ENUM.CLOSED
    const offset: number = activePageNum * MAX_PAGE_SIZE

    useEffect(() => {
        if (isMultiSelectMode && decisionEditorState.state !== undefined) {
            setDecisionEditorState({ state: undefined, data: undefined })
        }
    }, [isMultiSelectMode, decisionEditorState])

    const handleMassSubmission =
        (action_name: string): HandleMassSubmissionFunction =>
        (payload): void => {
            const { comment = '', post_time_window } = payload || {}
            if (selectedCampaignUuid) {
                dispatch(
                    massSubmission({
                        campaignUuid: selectedCampaignUuid,
                        uuids: massSubmissionUuids,
                        action_name,
                        comment,
                        post_time_window,
                        getSubmissionListPayload: {
                            offset,
                            stepUuid: selectedStep?.uuid,
                            ...submissionListParams,
                        },
                    }),
                )
            }
        }

    const onClickCheckBox = (apiRef: React.MutableRefObject<GridApi>, selectionModel: GridSelectionModel): void => {
        const selectedSubmissionUuids = rows
            .filter((row: ScreenshotRowsProps | EngagementScreenshotRowsProps | UrlRowsProps) =>
                selectionModel.some(selection => selection.toString() === row.id.toString()),
            )
            .map((row: ScreenshotRowsProps | EngagementScreenshotRowsProps | UrlRowsProps) => row.uuid)
        dispatch(setMassSubmissionUuids(selectedSubmissionUuids))
    }

    const handleClickExport = (viewType: SUBMISSION_VIEW_ENUM) => {
        if (!selectedCampaignUuid || !selectedStep) return
        return dispatch(
            exportSubmissions({
                uuid: selectedCampaignUuid,
                viewType: viewType as RequestExportSubmissionsPayload['viewType'],
                stepUuid: selectedStep.uuid,
            }),
        )
    }

    const onClickApprove = (submissionUuid: string): void => {
        setDecisionEditorState({ state: undefined, data: undefined })
        setDecisionEditorState(
            'Approve',
            submissions.filter((submission: SubmissionState) => submission.uuid === submissionUuid)[0],
        )
    }

    const onClickRequestEdit = (submissionUuid: string): void => {
        setDecisionEditorState({ state: undefined, data: undefined })
        setDecisionEditorState(
            'Request edits',
            submissions.filter((submission: SubmissionState) => submission.uuid === submissionUuid)[0],
        )
    }

    const onClickReject = (submissionUuid: string): void => {
        setDecisionEditorState({ state: undefined, data: undefined })
        setDecisionEditorState(
            'Reject',
            submissions.filter((submission: SubmissionState) => submission.uuid === submissionUuid)[0],
        )
    }

    const checkMultiSelectEnable = () => {
        if (!selectedStep || campaignEnded) return false
        return checkCanReviewSubmission(selectedStep.type)
    }
    const openBriefModal = () => {
        dispatch(closeModal())
        // bypass batch dispatch, reopen modal and make it center
        setTimeout(() => {
            dispatch(
                openModalWithProps({
                    type: GLOBAL_MODAL_TYPE.BRIEF_REQUIREMENT,
                    // task & step from sidebar, since toolbar review only exist in single task view
                    task: selectedTask || undefined,
                    stepId: selectedStep?.uuid,
                }),
            )
        }, 0)
    }

    return (
        <>
            <PartiTable
                title={selectedStep ? selectedStep.data.title + ' - ' + newT('statusLabel.pending', 'Pending') : ''}
                rowHeight={
                    selectedStep &&
                    // all engagement step will have image columns, so the row height need to be higher
                    (isEngagementStepType(selectedStep.type) ||
                        [STEP_TYPE_ENUM.SUBMIT_PROOF_IG_SCREENSHOT].includes(selectedStep.type))
                        ? 114
                        : 84
                }
                getRowHeight={() => 'auto'}
                columns={columns}
                rows={rows}
                isLoading={submissionLoading}
                enableMultiSelect={checkMultiSelectEnable()}
                onClickCheckBox={onClickCheckBox}
                hideToolbar={false}
                enableExport={!!rows.length}
                showFilter
                isNewStyle
                showMoreAction
                onClickApprove={onClickApprove}
                onClickRequestEdit={onClickRequestEdit}
                onClickReject={onClickReject}
                decisionEditorState={decisionEditorState}
                // when toolbar toolbar function is undefined, it will hide the mass action button
                onClickSubmissionToolbarApprove={!campaignEnded ? handleMassSubmission('approve') : undefined}
                onClickToolbarRequestEdit={!campaignEnded ? handleMassSubmission('request_edit') : undefined}
                onClickToolbarReject={!campaignEnded ? handleMassSubmission('reject') : undefined}
                onClickBriefRequirement={
                    selectedStep && isEngagementStepType(selectedStep.type) ? undefined : openBriefModal
                }
                viewType={TableViewType.SUBMISSIONS}
            />

            <ExportModal
                tableTitle={
                    selectedStep ? selectedStep.data.title + ' - ' + newT('statusLabel.pending', 'Pending') : ''
                }
                isRequesting={isSubmissionExportRequesting}
                onClickExport={() => handleClickExport(SUBMISSION_VIEW_ENUM.PENDING)}
            />
        </>
    )
}

const VerifyTableWrapper: React.FC<VerifyTableProps> = ({
    task,
    submissions,
    campaignUuid,
    showLinkInBio,
    handleEditTagChange,
    handleRejectTagChange,
}) => {
    const dispatch = useDispatch()
    const isEngagementStepType = useSelector(selectIsEngagementStepTypeHelper)
    const profiles = useSelector(selectPartiposterProfiles)
    const followerTierOptions = useSelector(selectFollowerTierOptions)
    const selectedCampaign = useSelector(selectSelectedCampaign)
    const taskTypes = useSelector(selectCampaignTaskTypes)
    const taskSettings = _.keyBy(taskTypes, 'name')
    const selectedTaskSettings = taskSettings[task.type] as TaskTypes
    const isMultiSelectMode = useSelector(selectIsMultiSelectMode)
    const selectedStep = useSelector(selectSidebarStep)
    const platformContentTypesByName = useSelector(selectTaskPlatformContentTypesByName)
    const canManageTag = usePermission(CampaignSubmission.ManageTag)
    const activePageNum = useSelector(selectActivePageNum)
    const submissionListParams = useSelector(selectSubmissionListParams)
    const campaignSummary = useSelector(selectSelectedCampaignSummary)
    const selectNextStep = useSelector(makeNextStepSelector)
    const sidebarTask = useSelector(selectSidebarTask)
    const selectedNextStep = selectNextStep(_, { step: selectedStep, task: sidebarTask })
    const { postMessage } = useBroadcastChannel('submission_review')

    // disable status for action column approve
    const disableApprove = (() => {
        if (!campaignSummary || !selectedStep) {
            return true
        }

        // if next step is not ended, return false
        if (selectedNextStep) {
            return dateIsInPast(selectedNextStep.time_window.end, campaignSummary.country)
        }

        // handle last step (no next step), always enable in last step
        return false
    })()

    // disable status for action column request edit
    const disableRequestEdit =
        selectedStep && campaignSummary ? dateIsInPast(selectedStep.time_window.end, campaignSummary.country) : false
    const isCampaignEnded = campaignSummary?.status === CAMPAIGN_STATUS_ENUM.CLOSED

    const getSubmissionListPayload = {
        offset: activePageNum * MAX_PAGE_SIZE,
        stepUuid: selectedStep?.uuid,
        ...submissionListParams,
    }

    const handleApprove = (submissionProgressUuid: string, comment: string, data: any): void => {
        dispatch(
            approveSubmission({
                campaignUuid,
                submissionProgressUuid,
                comment,
                data,
                getSubmissionListPayload,
                onSuccess: () => {
                    postMessage('reviewed_submission')
                },
            }),
        )
    }

    const handleEdit = useCallback(
        (submissionProgressUuid: string, comment: string) => {
            dispatch(
                requestEditsForSubmission({
                    campaignUuid,
                    submissionProgressUuid,
                    reason: comment,
                    getSubmissionListPayload,
                    onSuccess: () => {
                        postMessage('reviewed_submission')
                    },
                }),
            )
        },
        [campaignUuid],
    )

    const handleReject = useCallback(
        (submissionProgressUuid: string, comment: string) => {
            dispatch(
                rejectSubmission({
                    campaignUuid,
                    submissionProgressUuid,
                    reason: comment,
                    getSubmissionListPayload,
                    onSuccess: () => {
                        postMessage('reviewed_submission')
                    },
                }),
            )
        },
        [campaignUuid],
    )

    const Verify = useMemo(() => withDecisionEditor(VerifyTable), [VerifyTable])
    const searchablePlatforms = useSelector(selectSearchablePlatforms)

    if (campaignUuid && task) {
        switch (task.type) {
            case TASK_TYPE_ENUM.IG_STORY:
                const screenshotColumns: GridColDef[] = getScreenshotColumns({
                    showLinkInBio,
                    isMultiSelectMode,
                    canManageTag,
                    disableApprove,
                    disableRequestEdit,
                    isCampaignEnded,
                })
                const screenshotRows: ScreenshotRowsProps[] = getScreenshotRows(
                    campaignUuid,
                    submissions,
                    profiles,
                    task,
                    followerTierOptions,
                    isMultiSelectMode,
                    selectedCampaign ? selectedCampaign.country : undefined,
                )
                return (
                    <Verify
                        task={task}
                        submissions={submissions}
                        renderApproval={(data: any, handleClear: any): JSX.Element => (
                            <ApproveScreenshotSubmission
                                data={data}
                                handleApprove={handleApprove}
                                handleClear={handleClear}
                            />
                        )}
                        renderRequestEdits={(data: any, handleClear: any): JSX.Element => (
                            <EditSubmission
                                handleEdit={handleEdit}
                                data={data}
                                handleClear={handleClear}
                                handleEditTagChange={handleEditTagChange}
                            />
                        )}
                        renderRejection={(data: any, handleClear: any): JSX.Element => (
                            <RejectSubmission
                                data={data}
                                handleReject={handleReject}
                                handleClear={handleClear}
                                handleRejectTagChange={handleRejectTagChange}
                            />
                        )}
                        disableApprove={disableApprove}
                        disableRequestEdit={disableRequestEdit}
                        showLinkInBio={showLinkInBio}
                        columns={screenshotColumns}
                        rows={screenshotRows}
                    />
                )
            case TASK_TYPE_ENUM.IG_STORY_V02:
                const engagementTypesConfig = selectedTaskSettings.config.engagement_types
                const engagementTypes = Object.keys(task.payouts).filter(
                    payoutType => engagementTypesConfig && engagementTypesConfig.includes(payoutType),
                ) as EngagementId[]
                const engagementScreenshotColumns: GridColDef[] = getEngagementScreenshotColumns({
                    showLinkInBio,
                    engagementTypes,
                    isMultiSelectMode,
                    canManageTag,
                    disableApprove,
                    disableRequestEdit,
                    isCampaignEnded,
                })
                const engagementScreenshotRows: EngagementScreenshotRowsProps[] = getEngagementScreenshotRows(
                    campaignUuid,
                    submissions,
                    profiles,
                    task,
                    isMultiSelectMode,
                )
                return (
                    <Verify
                        task={task}
                        submissions={submissions}
                        renderApproval={(data: any, handleClear: any): JSX.Element => (
                            <ApproveEngagementScreenshotSubmission
                                data={data}
                                handleApprove={handleApprove}
                                handleClear={handleClear}
                                engagementTypes={engagementTypes}
                            />
                        )}
                        renderRequestEdits={(data: any, handleClear: any): JSX.Element => (
                            <EditSubmission
                                handleEdit={handleEdit}
                                data={data}
                                handleClear={handleClear}
                                handleEditTagChange={handleEditTagChange}
                            />
                        )}
                        renderRejection={(data: any, handleClear: any): JSX.Element => (
                            <RejectSubmission
                                data={data}
                                handleReject={handleReject}
                                handleClear={handleClear}
                                handleRejectTagChange={handleRejectTagChange}
                            />
                        )}
                        disableApprove={disableApprove}
                        disableRequestEdit={disableRequestEdit}
                        showLinkInBio={showLinkInBio}
                        columns={engagementScreenshotColumns}
                        rows={engagementScreenshotRows}
                    />
                )
            case TASK_TYPE_ENUM.IG_POST:
            case TASK_TYPE_ENUM.FB_POST:
                const urlColumns: GridColDef[] = getUrlColumns({
                    showLinkInBio,
                    selectedStep,
                    isMultiSelectMode,
                    canManageTag,
                    disableApprove,
                    disableRequestEdit,
                    isCampaignEnded,
                    task,
                })
                const urlRows: UrlRowsProps[] = getUrlRows(campaignUuid, submissions, profiles, task, isMultiSelectMode)
                return (
                    <Verify
                        task={task}
                        submissions={submissions}
                        renderApproval={(data: any, handleClear: any): JSX.Element => (
                            <ApproveUrlSubmission data={data} handleApprove={handleApprove} handleClear={handleClear} />
                        )}
                        renderRequestEdits={(data: any, handleClear: any): JSX.Element => (
                            <EditSubmission
                                handleEdit={handleEdit}
                                data={data}
                                handleClear={handleClear}
                                handleEditTagChange={handleEditTagChange}
                            />
                        )}
                        renderRejection={(data: any, handleClear: any): JSX.Element => (
                            <RejectSubmission
                                data={data}
                                handleReject={handleReject}
                                handleClear={handleClear}
                                handleRejectTagChange={handleRejectTagChange}
                            />
                        )}
                        disableApprove={disableApprove}
                        disableRequestEdit={disableRequestEdit}
                        showLinkInBio={showLinkInBio}
                        columns={urlColumns}
                        rows={urlRows}
                    />
                )
            case TASK_TYPE_ENUM.IG_POST_V02:
            case TASK_TYPE_ENUM.FB_POST_V02:
            case TASK_TYPE_ENUM.TIKTOK_POST:
            case TASK_TYPE_ENUM.IG_REEL:
            case TASK_TYPE_ENUM.XIAOHONGSHU_POST:
                switch (selectedStep && selectedStep.type) {
                    case STEP_TYPE_ENUM.POST_AND_SUBMIT_PROOF_INSTAGRAM_POST_URL:
                    case STEP_TYPE_ENUM.POST_AND_SUBMIT_PROOF_FACEBOOK_POST_URL:
                    case STEP_TYPE_ENUM.POST_AND_SUBMIT_PROOF_TIKTOK_POST_URL:
                    case STEP_TYPE_ENUM.POST_AND_SUBMIT_PROOF_INSTAGRAM_REEL_URL:
                    case STEP_TYPE_ENUM.POST_AND_SUBMIT_PROOF_XIAOHONGSHU_POST_URL:
                        const postAndUrlColumns: GridColDef[] = getUrlColumns({
                            showLinkInBio,
                            selectedStep,
                            isMultiSelectMode,
                            canManageTag,
                            disableApprove,
                            disableRequestEdit,
                            isCampaignEnded,
                            task,
                        })
                        const postAndUrlRows: UrlRowsProps[] = getUrlRows(
                            campaignUuid,
                            submissions,
                            profiles,
                            task,
                            isMultiSelectMode,
                        )
                        return (
                            <Verify
                                task={task}
                                submissions={submissions}
                                renderApproval={(data: any, handleClear: any): JSX.Element => (
                                    <ApprovePostAndUrlSubmission
                                        data={data}
                                        handleApprove={handleApprove}
                                        handleClear={handleClear}
                                    />
                                )}
                                renderRequestEdits={(data: any, handleClear: any): JSX.Element => (
                                    <EditSubmission
                                        handleEdit={handleEdit}
                                        data={data}
                                        handleClear={handleClear}
                                        handleEditTagChange={handleEditTagChange}
                                    />
                                )}
                                renderRejection={(data: any, handleClear: any): JSX.Element => (
                                    <RejectSubmission
                                        data={data}
                                        handleReject={handleReject}
                                        handleClear={handleClear}
                                        handleRejectTagChange={handleRejectTagChange}
                                    />
                                )}
                                disableApprove={disableApprove}
                                disableRequestEdit={disableRequestEdit}
                                showLinkInBio={showLinkInBio}
                                columns={postAndUrlColumns}
                                rows={postAndUrlRows}
                            />
                        )
                    case STEP_TYPE_ENUM.SUBMIT_ENGAGEMENT_RESULTS_AND_PROOF_IG_POST:
                    case STEP_TYPE_ENUM.SUBMIT_ENGAGEMENT_RESULTS_AND_PROOF_FACEBOOK_POST:
                    case STEP_TYPE_ENUM.SUBMIT_ENGAGEMENT_RESULTS_AND_PROOF_TIKTOK_POST:
                    case STEP_TYPE_ENUM.SUBMIT_ENGAGEMENT_RESULTS_AND_PROOF_INSTAGRAM_REEL:
                    case STEP_TYPE_ENUM.SUBMIT_ENGAGEMENT_RESULTS_AND_PROOF_XIAOHONGSHU_POST:
                        const engagementTypesConfig = selectedTaskSettings.config.engagement_types
                        const engagementTypes = Object.keys(task.payouts).filter(
                            payoutType => engagementTypesConfig && engagementTypesConfig.includes(payoutType),
                        ) as EngagementId[]
                        const engagementPostColumns: GridColDef[] = getEngagementPostColumns({
                            showLinkInBio,
                            engagementTypes,
                            isMultiSelectMode,
                            task,
                            canManageTag,
                            disableApprove,
                            disableRequestEdit,
                            isCampaignEnded,
                        })
                        const engagementPostRows: EngagementPostRowsProps[] = getEngagementPostRows(
                            campaignUuid,
                            submissions,
                            profiles,
                            task,
                            isMultiSelectMode,
                        )
                        return (
                            <Verify
                                task={task}
                                submissions={submissions}
                                renderApproval={(data: any, handleClear: any): JSX.Element => (
                                    <ApproveEngagementPostSubmission
                                        data={data}
                                        handleApprove={handleApprove}
                                        handleClear={handleClear}
                                        engagementTypes={engagementTypes}
                                    />
                                )}
                                renderRequestEdits={(data: any, handleClear: any): JSX.Element => (
                                    <EditSubmission
                                        handleEdit={handleEdit}
                                        data={data}
                                        handleClear={handleClear}
                                        handleEditTagChange={handleEditTagChange}
                                    />
                                )}
                                renderRejection={(data: any, handleClear: any): JSX.Element => (
                                    <RejectSubmission
                                        data={data}
                                        handleReject={handleReject}
                                        handleClear={handleClear}
                                        handleRejectTagChange={handleRejectTagChange}
                                    />
                                )}
                                disableApprove={disableApprove}
                                disableRequestEdit={disableRequestEdit}
                                showLinkInBio={showLinkInBio}
                                columns={engagementPostColumns}
                                rows={engagementPostRows}
                            />
                        )
                    default:
                        break
                }
            case TASK_TYPE_ENUM.CUSTOM:
                if (
                    selectedStep &&
                    isEngagementStepType(selectedStep.type) &&
                    task.platform_content_type &&
                    platformContentTypesByName[task.platform_content_type]
                ) {
                    const engagementTypesConfig = platformContentTypesByName[
                        task.platform_content_type
                    ].engagement_types.map(item => item.id)
                    const engagementTypes = Object.keys(task.payouts).filter(
                        payoutType =>
                            engagementTypesConfig && engagementTypesConfig.includes(payoutType as EngagementId),
                    ) as EngagementId[]
                    const columns = CustomEngagementTableDef.getColumns({
                        engagementTypes,
                        isMultiSelectMode,
                        stepType: selectedStep.type,
                        searchablePlatforms,
                        canManageTag,
                        disableApprove,
                        disableRequestEdit,
                        isCampaignEnded,
                    })
                    const rows = CustomEngagementTableDef.getRows(
                        campaignUuid,
                        submissions,
                        profiles,
                        isMultiSelectMode,
                    )
                    return (
                        <Verify
                            task={task}
                            submissions={submissions}
                            renderApproval={(data: any, handleClear: any): JSX.Element => (
                                <CustomEngagementTableDef.ApproveSubmission
                                    data={data}
                                    handleApprove={handleApprove}
                                    handleClear={handleClear}
                                    engagementTypes={engagementTypes}
                                    stepType={selectedStep.type}
                                />
                            )}
                            renderRequestEdits={(data: any, handleClear: any): JSX.Element => (
                                <EditSubmission
                                    handleEdit={handleEdit}
                                    data={data}
                                    handleClear={handleClear}
                                    handleEditTagChange={handleEditTagChange}
                                />
                            )}
                            renderRejection={(data: any, handleClear: any): JSX.Element => (
                                <RejectSubmission
                                    data={data}
                                    handleReject={handleReject}
                                    handleClear={handleClear}
                                    handleRejectTagChange={handleRejectTagChange}
                                />
                            )}
                            disableApprove={disableApprove}
                            disableRequestEdit={disableRequestEdit}
                            showLinkInBio={showLinkInBio}
                            columns={columns}
                            rows={rows}
                        />
                    )
                }
            default:
                return null
        }
    }
    return null
}

export default VerifyTableWrapper
